import { useCallback, useEffect, useMemo, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { cloneDeep } from 'lodash';

import apiRoutes from '../../../config/api/routes';
import { IChapterTrajectory, TrajectoryInfo } from '../../../config/api/models/Trajectory';
import useFetch from './useFetch';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

const useTrajectories = (bookId?: string, chapterId?: string) => {
  const { t } = useTranslation();
  const [trajectories, setTrajectories] = useState<IChapterTrajectory[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  // Get list
  const [getTrajectoriesResponse, getTrajectories] = useFetch<IChapterTrajectory[]>('');

  const fetchTrajectories = useCallback(() => {
    if (!bookId || !chapterId) return;
    getTrajectories({ path: apiRoutes.trajectories(bookId, chapterId) });
  }, [bookId, chapterId, getTrajectories]);

  useEffect(fetchTrajectories, [bookId, chapterId, fetchTrajectories]);

  useEffect(() => {
    if (!getTrajectoriesResponse.success) return;
    setTrajectories(getTrajectoriesResponse.data);
  }, [getTrajectoriesResponse]);

  // Update list
  const [patchTrajectoriesResponse, patchTrajectories] = useFetch<TrajectoryInfo[]>('', {
    method: 'PATCH',
  });

  const reorder = useCallback(
    ({ source, destination }: DropResult) => {
      if (!destination || !bookId) return;

      const trajectory = trajectories[source.index];
      const newTrajectories = cloneDeep(trajectories);
      newTrajectories.splice(source.index, 1);
      newTrajectories.splice(destination.index, 0, trajectory);

      setTrajectories(newTrajectories); // Set temporary until fetch gets back

      patchTrajectories({
        path: apiRoutes.reoderTrajectories(),
        body: { trajectories: newTrajectories },
      });
    },
    [bookId, patchTrajectories, trajectories]
  );

  useEffect(() => {
    if (!patchTrajectoriesResponse.success) return;
    fetchTrajectories();
  }, [bookId, chapterId, fetchTrajectories, patchTrajectoriesResponse.success]);

  // Add item
  const [postTrajectoriesResponse, postTrajectories] = useFetch('', { method: 'POST' });
  const add = useCallback(
    (trajectory: Partial<TrajectoryInfo>) => {
      if (!bookId || !chapterId) return;
      postTrajectories({
        body: trajectory,
        path: apiRoutes.addTrajectory(chapterId),
      });
    },
    [bookId, chapterId, postTrajectories]
  );

  useEffect(() => {
    if (!postTrajectoriesResponse.success) return;
    fetchTrajectories();
  }, [fetchTrajectories, postTrajectoriesResponse.success]);

  // Remove item
  const [deleteTrajectoryResponse, deleteTrajectory] = useFetch('', { method: 'DELETE' });
  const remove = useCallback(
    (trajectoryId: string) => {
      if (!bookId || !chapterId || !trajectoryId) return;
      deleteTrajectory({
        path: apiRoutes.trajectory(trajectoryId),
      });
    },
    [bookId, chapterId, deleteTrajectory]
  );

  useEffect(() => {
    if (!deleteTrajectoryResponse.success) return;
    fetchTrajectories();
  }, [deleteTrajectoryResponse.success, fetchTrajectories]);

  // Patch item
  const [patchTrajectoryResponse, patchTrajectory] = useFetch('', { method: 'PATCH' });
  const patch = useCallback(
    (trajectoryId: string, trajectoryPatch: Partial<TrajectoryInfo>) => {
      if (!bookId || !chapterId || !trajectoryId) return;
      patchTrajectory({
        path: apiRoutes.trajectory(trajectoryId),
        body: trajectoryPatch,
      });
    },
    [bookId, chapterId, patchTrajectory]
  );

  const [toggleDraftTrajectoryResponse, toggleDraftTrajectory] = useFetch('', {
    method: 'PATCH',
  });

  const [toggleFreezeTrajectoryResponse, togglefreezeTrajectory] = useFetch('', {
    method: 'PATCH',
  });

  const [togglePreviewTrajectoryResponse, togglePreviewTrajectory] = useFetch('', {
    method: 'PATCH',
  });

  const [moveTrajectoryResponse, moveTrajectory] = useFetch('', {
    method: 'POST',
  });

  const [copyTrajectoryResponse, copyTrajectory] = useFetch('', {
    method: 'POST',
  });

  useEffect(() => {
    if (!patchTrajectoryResponse.success) return;
    fetchTrajectories();
  }, [fetchTrajectories, patchTrajectoryResponse.success]);

  useEffect(() => {
    if (!toggleFreezeTrajectoryResponse.success) return;
    fetchTrajectories();
  }, [fetchTrajectories, toggleFreezeTrajectoryResponse.success]);

  useEffect(() => {
    if (!moveTrajectoryResponse.success) return;
    fetchTrajectories();
  }, [fetchTrajectories, moveTrajectoryResponse.success]);

  useEffect(() => {
    if (!togglePreviewTrajectoryResponse.success) return;
    fetchTrajectories();
  }, [fetchTrajectories, togglePreviewTrajectoryResponse.success]);

  useEffect(() => {
    if (!copyTrajectoryResponse.success) return;
    fetchTrajectories();
  }, [fetchTrajectories, copyTrajectoryResponse.success]);

  const toggleDraft = useCallback(
    (trajectoryId: string) => {
      toggleDraftTrajectory({
        path: apiRoutes.toggleTrajectoryDraft(trajectoryId),
      });
    },
    [toggleDraftTrajectory]
  );

  const toggleFreeze = useCallback(
    (trajectoryId: string) => {
      togglefreezeTrajectory({
        path: apiRoutes.toggleTrajectoryFreeze(trajectoryId),
      });
    },
    [togglefreezeTrajectory]
  );

  const togglePreview = useCallback(
    (trajectoryId: string) => {
      togglePreviewTrajectory({
        path: apiRoutes.toggleTrajectoryPreview(trajectoryId),
      });
    },
    [togglePreviewTrajectory]
  );

  const move = useCallback(
    (trajectoryId: string, chapterId: string) => {
      moveTrajectory({
        path: apiRoutes.moveTrajectory(trajectoryId),
        body: chapterId,
      });
    },
    [moveTrajectory]
  );

  const copy = useCallback(
    (trajectoryId: string, chapterId: string) => {
      copyTrajectory({
        path: apiRoutes.copyTrajectory(trajectoryId),
        body: chapterId,
      });
    },
    [copyTrajectory]
  );

  useEffect(() => {
    if (!toggleDraftTrajectoryResponse.success) return;
    fetchTrajectories();
  }, [toggleDraftTrajectoryResponse.success, fetchTrajectories]);

  useEffect(() => {
    if (!copyTrajectoryResponse.success) return;
    enqueueSnackbar(t('books.chapter.trajectories.snackbarCopyMessage'), {
      variant: 'success',
    });
  }, [copyTrajectoryResponse, enqueueSnackbar, t]);

  useEffect(() => {
    if (!moveTrajectoryResponse.success) return;
    enqueueSnackbar(t('books.chapter.trajectories.snackbarMoveMessage'), {
      variant: 'success',
    });
  }, [moveTrajectoryResponse, enqueueSnackbar, t]);

  const pending = useMemo(
    () =>
      getTrajectoriesResponse.pending ||
      patchTrajectoriesResponse.pending ||
      postTrajectoriesResponse.pending ||
      deleteTrajectoryResponse.pending ||
      patchTrajectoryResponse.pending ||
      toggleDraftTrajectoryResponse.pending ||
      toggleFreezeTrajectoryResponse.pending,
    [
      getTrajectoriesResponse.pending,
      patchTrajectoriesResponse.pending,
      postTrajectoriesResponse.pending,
      deleteTrajectoryResponse.pending,
      patchTrajectoryResponse.pending,
      toggleDraftTrajectoryResponse.pending,
      toggleFreezeTrajectoryResponse.pending,
    ]
  );

  const success = useMemo(
    () =>
      getTrajectoriesResponse.success &&
      patchTrajectoriesResponse.success &&
      postTrajectoriesResponse.success &&
      deleteTrajectoryResponse.success &&
      patchTrajectoryResponse.success &&
      toggleDraftTrajectoryResponse.success &&
      toggleFreezeTrajectoryResponse.success &&
      moveTrajectoryResponse.success,
    [
      getTrajectoriesResponse.success,
      patchTrajectoriesResponse.success,
      postTrajectoriesResponse.success,
      deleteTrajectoryResponse.success,
      patchTrajectoryResponse.success,
      toggleDraftTrajectoryResponse.success,
      toggleFreezeTrajectoryResponse.success,
      moveTrajectoryResponse.success,
    ]
  );

  const error = useMemo(
    () =>
      getTrajectoriesResponse.error ||
      patchTrajectoriesResponse.error ||
      postTrajectoriesResponse.error ||
      deleteTrajectoryResponse.error ||
      patchTrajectoryResponse.error ||
      toggleDraftTrajectoryResponse.error ||
      toggleFreezeTrajectoryResponse.error ||
      moveTrajectoryResponse.error,
    [
      deleteTrajectoryResponse.error,
      getTrajectoriesResponse.error,
      patchTrajectoriesResponse.error,
      patchTrajectoryResponse.error,
      postTrajectoriesResponse.error,
      toggleDraftTrajectoryResponse.error,
      toggleFreezeTrajectoryResponse.error,
      moveTrajectoryResponse.error,
    ]
  );

  const reason = useMemo(
    () =>
      (getTrajectoriesResponse.error && getTrajectoriesResponse.reason) ||
      (patchTrajectoriesResponse.error && patchTrajectoriesResponse.reason) ||
      (postTrajectoriesResponse.error && postTrajectoriesResponse.reason) ||
      (deleteTrajectoryResponse.error && deleteTrajectoryResponse.reason) ||
      (patchTrajectoryResponse.error && patchTrajectoryResponse.reason) ||
      (toggleFreezeTrajectoryResponse.error && toggleFreezeTrajectoryResponse.reason) ||
      (moveTrajectoryResponse.error && moveTrajectoryResponse.reason),
    [
      getTrajectoriesResponse,
      patchTrajectoriesResponse,
      postTrajectoriesResponse,
      deleteTrajectoryResponse,
      patchTrajectoryResponse,
      toggleFreezeTrajectoryResponse,
      moveTrajectoryResponse,
    ]
  );

  return {
    trajectories,
    operations: {
      add,
      remove,
      patch,
      reorder,
      toggleDraft,
      togglePreview,
      toggleFreeze,
      move,
      copy,
    },
    state: {
      pending,
      success,
      error,
    },
    reason,
  };
};

export default useTrajectories;
